import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  QueryList,
  ViewChildren,
  computed,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { Actions, ofType } from '@ngrx/effects';
import { GridComponent } from '@progress/kendo-angular-grid';
import { Observable, combineLatest } from 'rxjs';
import { map, skip, switchMap, take } from 'rxjs/operators';
import { SubSink } from 'subsink';
import { AbsencePlannerEventModel } from '../../core/models/absence-planner/absence-planner-event.model';
import { UserModel } from '../../core/models/user/user.model';
import { AppConfigService } from '../../core/services/app-config.service';
import {
  NotificationType,
  ShiveNotificationService,
} from '../../core/services/controls/shive-notification.service';
import { ShiveRenderService } from '../../core/services/rendering/shive-render.service';
import * as fromTimeBlockActions from '../../core/state/time-blocks/time-block-entries/time-blocks.actions';
import { UsersHttpService } from '../../core/state/users/users-http.service';
import { CalendarViews } from '../calendar/calendar-types';
import { TimeBlockItemBuilderService } from '../calendar/components/time-block/generation/time-block-item-builder.service';
import { ITimeBlockComponentItem } from '../calendar/components/time-block/types/time-block-component-item-types';
import { TimeBlockContentType } from '../calendar/components/time-block/types/time-block-types';
import { DateTimeHelper } from '../calendar/util/date-time-helper';
import { AbsencePlannerEventConverter } from './converter/absence-planner-event.converter';
import { AbsencePlannerEventsContainerDirective } from './directives/absence-planner-events-container.directive';
import { EventSlotDirective } from './directives/event-slot.directive';
import { AbsencePlannerEventCrudService } from './planner-event/services/absence-planner-event-crud.service';
import { AbsencePlannerService } from './services/absence-planner.service';
import {
  AbsencePlannerFixedColumnWidth,
  ActiveCreateEvent,
  ActiveDragOrResizeGrid,
  LockedMemberCellCSSClass,
} from './utils/absence-planner.constants';

@Component({
  selector: 'app-planner',
  templateUrl: './absence-planner.component.html',
  standalone: false,
  styleUrl: 'absence-planner.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AbsencePlannerComponent implements AfterViewInit, OnDestroy {
  @ViewChildren(AbsencePlannerEventsContainerDirective)
  plannerEventContainer!: QueryList<AbsencePlannerEventsContainerDirective>;
  @ViewChildren(EventSlotDirective)
  plannerEventSlots!: QueryList<EventSlotDirective>;
  gridElement = viewChild(GridComponent);
  gridComponent = viewChild(GridComponent, { read: ElementRef });
  public readonly absencePlannerService = inject(AbsencePlannerService);
  public readonly plannerEventCrudService = inject(AbsencePlannerEventCrudService);
  public readonly plannerFixedColumnWidth = AbsencePlannerFixedColumnWidth;
  public readonly DateTimeHelper = DateTimeHelper;
  public readonly faPlus = faPlus;
  public readonly cd = inject(ChangeDetectorRef);
  public readonly CalendarView = CalendarViews;
  public members = signal<UserModel[]>([]);
  public readonly plannerEventModelsComputed = computed(() => {
    // read the original array
    const events = this.absencePlannerService.plannerModel.plannerEventModels();

    // return a new reference by spreading
    return [...events];
  });
  public readonly LockedMemberCellCSSClass = LockedMemberCellCSSClass;
  protected readonly String = String;

  private minimumRowHeight = 0;
  private readonly usersHttpService = inject(UsersHttpService);
  private readonly appConfigService = inject(AppConfigService);
  private readonly shiveRenderService = inject(ShiveRenderService);
  private readonly timeBlockItemBuilderService = inject(TimeBlockItemBuilderService);
  private readonly actions$ = inject(Actions);
  private readonly shiveNotificationService = inject(ShiveNotificationService);
  private readonly subs = new SubSink();

  ngAfterViewInit(): void {
    this.bootstrap();
    this.initEvents();
    this.initHttpCallbacks();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private fillPlannerEventsSlotsMap(): void {
    const map = this.absencePlannerService.plannerModel.plannerEventsContainer;
    this.plannerEventContainer.forEach((eventPlannerSlot) => {
      const userId = eventPlannerSlot.member().id;
      const slotArr = map.get(userId)!;
      slotArr.push(eventPlannerSlot);
      map.set(userId, slotArr);
    });
  }

  private bootstrap(): void {
    this.absencePlannerService.plannerModel.plannerComponentInstance = this;

    combineLatest([
      this.usersHttpService.getUsers(false),
      this.appConfigService.getGlobalAppOptionsData(),
    ])
      .pipe(
        take(1),
        switchMap(([members, globalAppOptions]) => {
          this.absencePlannerService.initPlannerModel(members, globalAppOptions);
          this.members.set(members);
          this.cd.detectChanges();

          const rows = document.querySelectorAll('.k-grid-content-locked tr');

          if (rows.length > 0) {
            // Get the minimum row height (i.e. the height without planner events).
            this.minimumRowHeight = rows[0].getBoundingClientRect().height;
          }

          // Initialize empty planner container and slots map
          members.forEach((member) => {
            const memberAssociatedContainer = this.plannerEventContainer.filter(
              (slot) => slot.member().id === member.id,
            );
            this.absencePlannerService.plannerModel.plannerEventsContainer.set(member.id, [
              ...memberAssociatedContainer,
            ]);

            const memberAssociatedSlots = this.plannerEventSlots.filter(
              (slot) => slot.member().id === member.id,
            );
            this.absencePlannerService.plannerModel.eventSlots.set(member.id, [
              ...memberAssociatedSlots,
            ]);
          });

          return this.loadAbsencePlannerEvents(members);
        }),
      )
      .subscribe((plannerEventModels: AbsencePlannerEventModel[]) => {
        this.setPlannerSlotWidth();
        this.fillPlannerEventsSlotsMap();
        this.buildPlannerEventComponents(plannerEventModels);

        this.reRenderGridRows();
        this.absencePlannerService.plannerModel.htmlGrid = this.gridComponent()!.nativeElement;
      });
  }

  private setPlannerSlotWidth() {
    const plannerModel = this.absencePlannerService.plannerModel;
    const firstContainer = this.plannerEventContainer.first.elementRef.nativeElement;
    plannerModel.slotWidth = firstContainer.getBoundingClientRect().width;
  }

  private initEvents() {
    this.subs.sink = this.absencePlannerService.createEventInteractionInProgress$
      .pipe(skip(1))
      .subscribe((createEvent) => {
        if (createEvent) {
          this.shiveRenderService.addClass(this.gridComponent()!.nativeElement, ActiveCreateEvent);
        } else {
          this.shiveRenderService.removeClass(
            this.gridComponent()!.nativeElement,
            ActiveCreateEvent,
          );
        }
      });

    this.subs.sink = this.absencePlannerService.dragOrResizeInteractionInProgress$
      .pipe(skip(1))
      .subscribe((dragOrResizeInProgress) => {
        if (dragOrResizeInProgress) {
          this.shiveRenderService.addClass(
            this.gridComponent()!.nativeElement,
            ActiveDragOrResizeGrid,
          );
        } else {
          this.shiveRenderService.removeClass(
            this.gridComponent()!.nativeElement,
            ActiveDragOrResizeGrid,
          );
        }
      });

    this.initEventsReloading();
  }

  private buildPlannerEventComponents(plannerEventModels: AbsencePlannerEventModel[]): void {
    plannerEventModels.forEach((plannerEventModel) => {
      this.plannerEventCrudService.addPlannerEvent(plannerEventModel);
    });
    this.absencePlannerService.calculateMaxParallelIndexPerGroup();
  }

  private loadAbsencePlannerEvents(members: UserModel[]): Observable<AbsencePlannerEventModel[]> {
    // Take the previous and the next months of the current month to load the necessary time blocks.
    const plannerStart = DateTimeHelper.subMonths(
      this.absencePlannerService.plannerModel.plannerStartOfMonth,
      1,
    );
    const plannerEnd = DateTimeHelper.addMonths(
      this.absencePlannerService.plannerModel.plannerEndOfMonth,
      1,
    );

    return this.absencePlannerService.timeBlockHttpService
      .fetchTimeBlocks(
        plannerStart,
        plannerEnd,
        members.map((member) => member.id),
        null,
        null,
        [TimeBlockContentType.Absence],
      )
      .pipe(
        map((timeBlocks: ITimeBlockComponentItem[]) => {
          const clonedTimeBlocks = timeBlocks.map((tb) =>
            tb.clone(this.timeBlockItemBuilderService),
          );
          return AbsencePlannerEventConverter.toPlannerEventModel(clonedTimeBlocks);
        }),
      );
  }

  private initHttpCallbacks(): void {
    this.subs.sink = this.actions$
      .pipe(
        ofType(
          fromTimeBlockActions.timeBlockAdded,
          fromTimeBlockActions.timeBlockUpdated,
          fromTimeBlockActions.timeBlockDeleted,
        ),
      )
      .subscribe(({ payload, type }) => {
        // Render all altered time blocks returned from the server.
        if (type === fromTimeBlockActions.timeBlockAdded.type) {
          const newPlannerEventModels = this.convertTimeBlocks([...payload.modifiedTimeBlockItems]);
          this.plannerEventCrudService.addPlannerEvent(newPlannerEventModels[0]);
        } else if (type === fromTimeBlockActions.timeBlockUpdated.type) {
          const plannerEventModels = this.convertTimeBlocks([...payload.modifiedTimeBlockItems]);
          const updatedPlannerEvent = plannerEventModels[0];

          const toBeDeletedPlannerEvent = this.plannerEventCrudService.selectPlannerEvent(
            updatedPlannerEvent.id,
          );
          // Update planner event by removing the old planner component and adding the updated one to the model.
          this.plannerEventCrudService.removePlannerEvent(toBeDeletedPlannerEvent);
          this.plannerEventCrudService.addPlannerEvent(updatedPlannerEvent);
        } else {
          // Delete
          const toBeDeletedPlannerEvent = this.plannerEventCrudService.selectPlannerEvent(
            payload.deletedTimeBlockId,
          );
          this.plannerEventCrudService.removePlannerEvent(toBeDeletedPlannerEvent);
        }

        this.absencePlannerService.calculateMaxParallelIndexPerGroup();
        this.reRenderGridRows();
        this.shiveNotificationService.show(NotificationType.Success);
      });
  }

  private convertTimeBlocks(modifiedTimeBlockItems: ITimeBlockComponentItem[]) {
    const clonedTimeBlockItmes = modifiedTimeBlockItems.map((item) =>
      item.clone(this.timeBlockItemBuilderService),
    );
    return AbsencePlannerEventConverter.toPlannerEventModel([...clonedTimeBlockItmes]);
  }

  private reRenderGridRows() {
    const rows = document.querySelectorAll('.k-grid-content-locked tr');

    if (rows.length === 0) {
      return;
    }

    // Render planner events here to obtain their heights.
    this.cd.detectChanges();

    rows.forEach((row) => {
      const memberCell = row.querySelector(`.${LockedMemberCellCSSClass}`)!;
      const memberId = +this.shiveRenderService.getAttribute(memberCell, 'data-member-id');
      const rowHeight = this.absencePlannerService.calculateRowHeights(
        +memberId,
        this.minimumRowHeight,
      );

      if (rowHeight < 0) {
        return;
      }

      this.shiveRenderService.setStyle(memberCell, 'height', `${rowHeight}px`);
    });
    this.gridElement()!.autoFitColumns();
  }

  private initEventsReloading(): void {
    this.subs.sink = this.absencePlannerService.reloadPlannerEvents$
      .pipe(
        switchMap(() => {
          this.absencePlannerService.clearAllPlannerEventComponents();
          this.setPlannerSlotWidth();

          return this.loadAbsencePlannerEvents(this.absencePlannerService.plannerModel.members);
        }),
      )
      .subscribe((plannerEventModels: AbsencePlannerEventModel[]) => {
        this.buildPlannerEventComponents(plannerEventModels);
        this.cd.detectChanges();
      });
  }
}
